import { useApi, useReportingTech } from '../../hooks';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { Input } from '../inputs';

import type { IVerifyTechEmailAddressResponse } from '../../types';
import type { IToggle } from '../../hooks';
import { FC, useContext, useEffect, useState } from 'react';
import { PasswordWarning } from '../../features/auth/components';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Localization } from '../../config/localization/localization';
import { LanguageSelectorContext } from '../../context/LanguageSelectorContext';

interface ITechnicianFieldsProps {
    uniqueEmailToggle: IToggle;
}

export const TechnicianFields: FC<ITechnicianFieldsProps> = ({ uniqueEmailToggle }) => {
    const formContext = useFormContext();
    const { reportingTech } = useReportingTech();
    const { verifyTechEmailAddress } = useApi();
    const [originalTechEmailAddress, setOriginalTechEmailAddress] = useState<string | null>(null);
    const [techEmailAddressChanged, setTechEmailAddressChanged] = useState<boolean>(false);

    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    useEffect(() => {
        setOriginalTechEmailAddress(formContext.getValues('techEmailAddress'));
    }, []);

    const onVerifyEmailAddress = (response: IVerifyTechEmailAddressResponse | null) => {
        if (response?.reportingTech === null) {
            uniqueEmailToggle.on();
            formContext.clearErrors('techEmailAddress');
        } else {
            if (
                reportingTech?.emailAddress?.trim().toUpperCase() ===
                formContext.getValues('techEmailAddress')?.trim()?.toUpperCase()
            )
                return;
            uniqueEmailToggle.off();
            formContext.setError('techEmailAddress', {
                message: localization.translate('technician.uniqueEmailError'),
            });
        }
    };

    const { mutate: verifyEmail } = useMutation({
        mutationFn: verifyTechEmailAddress,
        onSuccess: onVerifyEmailAddress,
    });

    const onBlurEmailAddress = () => {
        const email: string = formContext.getValues('techEmailAddress');
        setTechEmailAddressChanged(email !== originalTechEmailAddress);
        if (!email || email?.trim().length === 0) return;
        verifyEmail({
            clientId: -1,
            marketId: -1,
            prefixText: email,
        });
    };

    return (
        <div>
            <h1 className={'min-w-[250px] border-b pb-2 text-2xl font-semibold text-brand-500 dark:text-brand-100'}>
                {localization.translate('technician.header')}
            </h1>

            <div className={'flex w-full flex-col items-center space-y-3 sm:mt-3'}>
                {/* TECH ID */}
                <Input
                    className={'text-gray-400'}
                    inputOptions={{
                        isDisabled: true,
                        label: localization.translate('technician.username'),
                        name: 'techId',
                    }}
                    tooltip={localization.translate('technician.usernameTooltip')}
                />

                {/* FIRST NAME */}
                <Input
                    inputOptions={{
                        isRequired: true,
                        label: localization.translate('technician.firstName'),
                        name: 'techFirstName',
                    }}
                    tooltip={localization.translate('technician.firstNameTooltip')}
                />

                {/* LAST NAME */}
                <Input
                    inputOptions={{
                        isRequired: true,
                        label: localization.translate('technician.lastName'),
                        name: 'techLastName',
                    }}
                    tooltip={localization.translate('technician.lastNameTooltip')}
                />

                {/* PHONE NUMBER */}
                <Input
                    inputOptions={{
                        isRequired: true,
                        label: localization.translate('technician.phoneNumber'),
                        name: 'techPhoneNumber',
                    }}
                    tooltip={localization.translate('technician.phoneNumberTooltip')}
                    type={'phone'}
                />

                {/* EMAIL ADDRESS */}
                <Input
                    inputOptions={{
                        autoComplete: 'email',
                        isRequired: true,
                        label: localization.translate('technician.emailAddress'),
                        name: 'techEmailAddress',
                        onBlur: onBlurEmailAddress,
                    }}
                    tooltip={localization.translate('technician.emailAddressTooltip')}
                />

                {techEmailAddressChanged && (
                    <>
                        <div
                            className={'flex flex-row items-center bg-orange-100 p-1.5 text-gray-900 dark:bg-orange-600 dark:text-gray-100'}>
                            <ExclamationTriangleIcon className={'mr-1 h-5 w-5 flex-shrink-0'} />
                            {localization.translate('registration.changeEmailMessage')}
                        </div>

                        {/* PASSWORD */}
                        <Input
                            inputOptions={{
                                isRequired: true,
                                label: localization.translate('registration.password'),
                                name: 'password',
                                type: 'password',
                            }}
                            tooltip={localization.translate('registration.passwordTooltip')}
                        />

                        {/* CONFIRM PASSWORD */}
                        <Input
                            inputOptions={{
                                isRequired: true,
                                label: localization.translate('registration.confirmPassword'),
                                name: 'confirmPassword',
                                type: 'password',
                            }}
                            tooltip={localization.translate('registration.confirmPasswordTooltip')}
                        />

                        {/* PASSWORD FORMAT WARNING */}
                        <PasswordWarning />
                    </>
                )}
            </div>
        </div>
    );
};